import { fetchShopifyConnections, shopifySectionHTML } from '../services/shopifyConnections.js';
import { loadingState } from '../core/loadingState.js';
/**
 * settings-integrations-route.js — the Integrations HUB (/settings/integrations).
 *
 * A grid of provider tiles: brand logo, name, one-line value prop, live status
 * pill. Every tile is a plain anchor into that provider's detail page
 * (#/settings/integrations/<slug>), where ALL configuration lives — the hub
 * carries no action handlers at all. Coming-soon tiles render muted and inert.
 *
 * OAuth returns still land here (the server redirect target is shared by four
 * OAuth app registrations): consumeReturnParams() toasts the outcome, strips
 * the search string, parks it via setPendingReturn, and forwards to the
 * provider's detail page, which auto-opens the right picker.
 *
 * Personas: read-only members see the same tiles (status is information, not
 * an affordance); a true 403 still renders the lock explainer. Reseller
 * portfolio mode gets the client picker, unchanged.
 */

import {
  PROVIDERS, CATEGORIES, AUTOMATION_TILES, esc, toast, apiBase, emptyState,
  fetchIntegrations, connFor, statusOf, statusPill, logoHTML,
  ensureStyles, setPendingReturn,
} from '../services/integrations-shared.js';
import { inPortfolio, ensurePortfolioStyles, pickerPageHTML, initPicker } from '../services/portfolio-view.js';
import { mountLeadConnectionReturn, clearLeadBootRoute } from '../core/leadWorkspaceMemory.js';

export function render() {
  ensureStyles();
  ensurePortfolioStyles();
  if (inPortfolio()) {
    return pickerPageHTML({
      section: 'settings-integrations', title: 'Integrations',
      description: 'Pick a client to manage their connections.',
    });
  }
  return `
    <div class="page-shell" data-section="settings-integrations">
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Integrations</h1>
          <p class="page-description">Connected accounts for this workspace.</p>
        </div>
      </div>
      <div id="shopify-connections">${loadingState('cards', 'Loading Shopify connections')}</div>
      <div id="intg-body">
        <div class="dashboard-card intg-card"><div class="card-body">${loadingState('cards', 'Loading connections')}</div></div>
      </div>
    </div>`;
}

export async function init() {
  clearLeadBootRoute();
  if (inPortfolio()) {
    const fmtN = (n) => Number(n || 0).toLocaleString();
    return initPicker(apiBase(), [
      { h: 'Magnets', td: (r) => `${fmtN(r.magnets.active)}<span style="color:var(--color-text-subtle,#6C7C9A)">/${fmtN(r.magnets.total)}</span>` },
      { h: 'Leads · wk', td: (r) => fmtN(r.leads.this_week) },
    ], { emptyMsg: 'No client orgs yet. Create one under Clients first.' });
  }
  if (consumeReturnParams()) return; // forwarded to a detail page
  mountLeadConnectionReturn(document.querySelector('[data-section="settings-integrations"]'));
  await Promise.all([load(), loadShopify()]);
}

// Every OAuth provider (CallRail has no redirect flow — pasted key).
const PROVIDER_LABEL = Object.fromEntries(
  Object.values(PROVIDERS).filter((p) => p.connect).map((p) => [p.slug, p.label]),
);

/** OAuth outcome rides the search string (survives the hash router). Returns
 * true when the outcome was forwarded to a provider detail page. */
function consumeReturnParams() {
  try {
    const sp = new URLSearchParams(window.location.search);
    const connected = sp.get('connected');
    const errorProvider = sp.get('error') ? (sp.get('provider') || 'google_ads') : null; // pre-explicit-provider callbacks defaulted to Ads
    let target = null;
    // Own-key guards: these params are attacker-controlled, and a bare lookup
    // on a plain object resolves prototype keys ('constructor', 'toString'),
    // which would truthy-pass and navigate to /settings/integrations/undefined.
    if (connected && Object.hasOwn(PROVIDER_LABEL, connected)) {
      // The Microsoft flow can carry a second outcome: the tenant-wide
      // in-magnet booking approval. Say what actually happened to each half.
      const consent = connected === 'ms_bookings' ? sp.get('consent') : null;
      if (consent === 'granted') toast('In-magnet booking approved for your organization.', 'success');
      else if (consent === 'declined') toast('Microsoft Bookings is connected. In-magnet booking still needs the one-time approval, see step 2.', 'warning');
      else toast(`${PROVIDER_LABEL[connected]} connected.`, 'success');
      setPendingReturn({ slug: connected, connected: true });
      target = Object.hasOwn(PROVIDERS, connected) ? PROVIDERS[connected] : null;
    } else if (errorProvider) {
      const label = Object.hasOwn(PROVIDER_LABEL, errorProvider) ? PROVIDER_LABEL[errorProvider] : errorProvider;
      toast(`${label} connection failed (${sp.get('error')}).`, 'error');
      target = Object.hasOwn(PROVIDERS, errorProvider) ? PROVIDERS[errorProvider] : null;
    } else {
      return false;
    }
    window.history.replaceState(null, '', window.location.pathname + window.location.hash);
    if (target) {
      window.router?.navigate(`/settings/integrations/${target.urlSlug}`);
      return true;
    }
    return false;
  } catch { /* cosmetic only */ return false; }
}

async function loadShopify() {
  const body = document.getElementById('shopify-connections');
  const data = await fetchShopifyConnections();
  if (body?.isConnected) body.innerHTML = shopifySectionHTML(data);
}

async function load() {
  const body = document.getElementById('intg-body');
  try {
    const { denied, providers, configured } = await fetchIntegrations();
    if (denied) {
      if (body) body.innerHTML = `<div class="dashboard-card intg-card"><div class="card-body">${emptyState('lock', 'Integrations are managed by workspace admins.')}</div></div>`;
      return;
    }
    if (body) body.innerHTML = gridHTML(providers, configured);
  } catch (e) {
    if (body) body.innerHTML = `<div class="dashboard-card intg-card"><div class="card-body">${emptyState('error', 'Could not load integrations.')}</div></div>`;
  }
}

function tileHTML(p, conn, configured) {
  const status = statusOf(p, conn, configured);
  return `
    <a class="intg-tile" href="#/settings/integrations/${p.urlSlug}" data-provider="${p.slug}">
      <div class="intg-tile__head">
        ${logoHTML(p)}
        <div class="intg-tile__titles">
          <div class="intg-tile__name">${esc(p.label)}</div>
          <div class="intg-tile__sub">${esc(p.card.sub)}</div>
        </div>
      </div>
      <p class="intg-tile__desc">${esc(tileDesc(p, conn, status))}</p>
      <div class="intg-tile__foot">
        ${statusPill(status, conn)}
        <span class="intg-tile__go material-symbols-outlined" aria-hidden="true">arrow_forward</span>
      </div>
    </a>`;
}

/** One line per tile: connected identity when there is one, value prop otherwise. */
function tileDesc(p, conn, status) {
  if (status === 'connected') {
    const acct = conn?.external_account || {};
    if (p.slug === 'calendly') return acct.name ? `Connected as ${acct.name}.` : 'Connected.';
    if (p.slug === 'callrail') {
      const scope = [acct.account_name, acct.company_name].filter(Boolean).join(' · ');
      return scope ? `Connected to ${scope}.` : 'Connected.';
    }
    if (p.slug === 'hubspot') return acct.hub_domain ? `Connected to ${acct.hub_domain}.` : 'Connected.';
    if (p.slug === 'zoho') return acct.location ? `Connected to Zoho CRM (${String(acct.location).toUpperCase()}).` : 'Connected to Zoho CRM.';
    if (p.slug === 'calcom') return acct.username ? `Connected as ${acct.username}.` : 'Connected.';
    if (p.slug === 'salesforce') return acct.username ? `Connected as ${acct.username}.` : 'Connected.';
    if (p.slug === 'gohighlevel') return acct.location_name ? `Connected to ${acct.location_name}.` : 'Connected.';
    if (p.slug === 'ms_bookings') return acct.upn ? `Connected as ${acct.upn}.` : 'Connected.';
    const n = p.card.reachableCount(acct);
    return n ? `${n} ${p.slug === 'google_analytics' ? (n === 1 ? 'property' : 'properties') : (n === 1 ? 'account' : 'accounts')} feeding reporting.` : 'Connected.';
  }
  if (status === 'broken') return 'The connection stopped working. Open to reconnect.';
  // Tile copy is its own tier: one short sentence sized to the box. The fuller
  // description belongs on the detail page, after the click.
  return p.card.tile || p.card.sub;
}

/** The Automation tile: the org webhook (also the Zapier path). Configured on
 * the Notifications page, so the tile is an anchor there, not a detail page. */
function automationTileHTML(t) {
  return `
    <a class="intg-tile" href="${esc(t.href)}" data-provider="${esc(t.key || 'webhook')}">
      <div class="intg-tile__head">
        <span class="intg-mark">
          <img class="intg-logo" src="${esc(t.logo)}" alt="" loading="lazy"
               onerror="this.style.display='none';this.parentElement.querySelector('.intg-logo-fallback').style.display=''">
          <span class="material-symbols-outlined intg-logo-fallback" aria-hidden="true" style="display:none">${t.icon}</span>
        </span>
        <div class="intg-tile__titles">
          <div class="intg-tile__name">${esc(t.label)}</div>
          <div class="intg-tile__sub">${esc(t.sub)}</div>
        </div>
      </div>
      <p class="intg-tile__desc">${esc(t.tile)}</p>
      <div class="intg-tile__foot">
        <span class="intg-state">Set up under Notifications</span>
        <span class="intg-tile__go material-symbols-outlined" aria-hidden="true">arrow_forward</span>
      </div>
    </a>`;
}

function gridHTML(providers, configured) {
  return CATEGORIES.map((cat) => {
    const tiles = cat.key === 'automation'
      ? AUTOMATION_TILES.map(automationTileHTML).join('')
      : Object.values(PROVIDERS)
          .filter((p) => p.category === cat.key)
          .map((p) => tileHTML(p, connFor(providers, p.slug), !!configured?.[p.slug]))
          .join('');
    if (!tiles) return '';
    return `
      <section class="intg-section" data-category="${esc(cat.key)}">
        <h2 class="intg-section__title">${esc(cat.title)}</h2>
        <p class="intg-section__intro">${esc(cat.intro)}</p>
        <div class="intg-grid">${tiles}</div>
      </section>`;
  }).join('');
}

export default { render, init };
